import { Button, DataTable, PageHead, Tabs } from "@nabble/ui";
import { useMemo, useState } from "react";
import { type AdminDataRequest, fetchAdminDataRequests } from "../../../api/adminDataRequests";
import { useLoadedData } from "../../../hooks/useLoadedData";
import { filterRequests, isAnswered, type RequestFilter } from "../../../model/dataRequests";
import { dataRequestColumns } from "./dataRequestColumns";
import { RequestDrawer } from "./RequestDrawer";

const NO_REQUESTS: AdminDataRequest[] = [];

/** Personal data requests (Chile Law 21.719): answer each one within 30 calendar days, extendable once. */
export default function AdminDataRequestsPage(): React.JSX.Element {
  const { data, loading, loadError, reload } = useLoadedData(fetchAdminDataRequests, "We couldn't load the requests.");
  const requests = data ?? NO_REQUESTS;
  const [filter, setFilter] = useState<RequestFilter>("OPEN");
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const rows = useMemo(() => filterRequests(requests, filter), [requests, filter]);
  const openCount = requests.filter((r) => !isAnswered(r.status)).length;
  const selected = requests.find((r) => r.id === selectedId) ?? null;
  const columns = useMemo(() => dataRequestColumns((r) => setSelectedId(r.id)), []);

  return (
    <div>
      <PageHead titleHidden title="Data requests" />

      <div style={{ marginBottom: "var(--space-4)" }}>
        <Tabs
          ariaLabel="Show requests"
          value={filter}
          onChange={(id) => setFilter(id as RequestFilter)}
          items={[
            { id: "OPEN", label: "Open", count: openCount },
            { id: "ANSWERED", label: "Answered", count: requests.length - openCount },
            { id: "ALL", label: "All", count: requests.length },
          ]}
        />
      </div>

      <DataTable<AdminDataRequest>
        id="admin-data-requests"
        rowKey="id"
        loading={loading}
        rows={rows}
        columns={columns}
        exportName="data-requests"
        searchPlaceholder="Search requests"
        onRowClick={(r) => setSelectedId(r.id)}
        error={
          loadError
            ? {
                title: "We couldn't load the requests",
                description: loadError,
                action: <Button label="Try again" variant="secondary" icon="refresh" onClick={reload} />,
              }
            : null
        }
        empty={{
          icon: "shield",
          title: filter === "OPEN" ? "No open requests" : "No requests here",
          description:
            filter === "OPEN"
              ? "When someone asks to see, correct or delete their personal data, the request shows up here."
              : "No requests match this filter.",
        }}
      />

      {selected ? <RequestDrawer key={selected.id} request={selected} onClose={() => setSelectedId(null)} onChanged={reload} /> : null}
    </div>
  );
}
